<template>
  <div id="app">
    <div class="app-content">
      <router-view/>
    </div>
    
    <!-- Legal Footer (only show on non-chat pages) -->
    <LegalFooter v-if="shouldShowFooter" @language-changed="onLanguageChanged" />
    
    <!-- Cookie Consent Banner -->
    <CookieConsent ref="cookieConsent" @consent-updated="onConsentUpdated" />
  </div>
</template>

<script>
import LegalFooter from './components/LegalFooter.vue'
import CookieConsent from './components/CookieConsent.vue'

export default {
  name: 'App',
  components: {
    LegalFooter,
    CookieConsent
  },
  computed: {
    shouldShowFooter() {
      // Show footer on legal pages, hide on chat page for cleaner UI
      const legalPages = ['imprint', 'privacy', 'disclaimer', 'cookie-settings', 'cookie-policy']
      return legalPages.includes(this.$route.name)
    }
  },
  mounted() {
    this.initializeLanguage()
    this.setupMetaTags()
  },
  watch: {
    '$route'(to) {
      this.updateMetaTags(to)
    },
    '$i18n.locale'() {
      this.updateDocumentLanguage()
    }
  },
  methods: {
    initializeLanguage() {
      // Load saved language preference
      try {
        const savedLanguage = localStorage.getItem('preferred-language')
        if (savedLanguage && ['en', 'de'].includes(savedLanguage)) {
          this.$i18n.locale = savedLanguage
        } else {
          // Detect browser language
          const browserLang = navigator.language.split('-')[0]
          if (browserLang === 'de') {
            this.$i18n.locale = 'de'
          }
        }
      } catch {
        // Handle storage errors gracefully
      }
      
      this.updateDocumentLanguage()
    },
    
    updateDocumentLanguage() {
      document.documentElement.lang = this.$i18n.locale
    },
    
    setupMetaTags() {
      // Set initial meta tags
      this.updateMetaTags(this.$route)
    },
    
    updateMetaTags(route) {
      // Update page title and meta tags based on route
      const baseTitle = 'ServoGPT'
      
      if (route.meta && route.meta.title) {
        const localizedTitle = this.$t(`nav.${route.name}`) || route.meta.title
        document.title = `${localizedTitle} - ${baseTitle}`
      } else {
        document.title = baseTitle
      }
      
      // Update robots meta tag
      let robotsMeta = document.querySelector('meta[name="robots"]')
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta')
        robotsMeta.name = 'robots'
        document.head.appendChild(robotsMeta)
      }
      
      if (route.meta && route.meta.robots) {
        robotsMeta.content = route.meta.robots
      } else {
        robotsMeta.content = 'index, follow'
      }
    },
    
    onLanguageChanged() {
      // Handle language change from footer
      this.updateDocumentLanguage()
      this.updateMetaTags(this.$route)
    },
    
    onConsentUpdated(preferences) {
      // Handle cookie consent updates
      console.log('Cookie consent updated:', preferences)

      // Matomo consent is already handled by the CookieConsent component
      // This handler can be used for other global state updates if needed
    }
  }
}
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-content {
  flex: 1;
}

/* Global accessibility improvements */
*:focus {
  outline: 2px solid #02F0F2;
  outline-offset: 2px;
}

/* Skip link for screen readers */
.skip-link {
  position: absolute;
  top: -40px;
  left: 6px;
  background: #000;
  color: #fff;
  padding: 8px;
  text-decoration: none;
  border-radius: 0 0 4px 4px;
  z-index: 10001;
}

.skip-link:focus {
  top: 0;
}

/* Ensure proper contrast for legal content */
.legal-content,
.legal-section {
  color: #333;
}

/* Print styles */
@media print {
  .legal-footer,
  .cookie-banner,
  .cookie-modal-overlay {
    display: none !important;
  }
  
  .legal-page {
    background: white !important;
    color: black !important;
  }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  #app {
    background: #fff;
    color: #000;
  }
  
  .legal-content {
    background: #fff !important;
    color: #000 !important;
    border-color: #000 !important;
  }
}
</style>
